/**
 * ThumbnailGenerator — VIDEO_ALPHA preview thumbnails for the asset bar. web-core's
 * alpha-video layout is color-left / alpha-right, so we draw the left (colour) half
 * of the current video frame to a 2D canvas → data URL (throttled).
 */

import { AssetTypes } from '@shared/constants';
import type { EventBus } from '@shared/events/EventBus';
import type { EditorEvents } from '@shared/events/EventTypes';
import type { AssetManager } from './AssetManager';

export class ThumbnailGenerator {
  private assetManager: AssetManager;
  private events: EventBus<EditorEvents>;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private timeUntilUpdate = 0;
  private period: number;

  constructor(assetManager: AssetManager, events: EventBus<EditorEvents>, width = 128, height = 128, period = 1) {
    this.assetManager = assetManager;
    this.events = events;
    this.period = period;
    this.canvas = document.createElement('canvas');
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = this.canvas.getContext('2d');
  }

  update(delta: number): void {
    this.timeUntilUpdate += delta;
    if (this.timeUntilUpdate > this.period) {
      this.timeUntilUpdate = 0;
      const thumbnails = this.getThumbnails();
      if (Object.keys(thumbnails).length > 0) {
        this.events.emit('thumbnails:generated', { thumbnails });
      }
    }
  }

  getThumbnails(): Record<string, string> {
    const out: Record<string, string> = {};
    if (!this.ctx) return out;
    for (const asset of this.assetManager.assets) {
      if (asset.assetType !== AssetTypes.VIDEO_ALPHA) continue;
      const video = asset.videoElement;
      if (!video || video.videoWidth === 0) continue;
      // Colour region is the left half of the side-by-side alpha video.
      const sw = video.videoWidth / 2;
      const sh = video.videoHeight;
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      try {
        this.ctx.drawImage(video, 0, 0, sw, sh, 0, 0, this.canvas.width, this.canvas.height);
        out[asset.assetID] = this.canvas.toDataURL();
      } catch {
        /* drawImage/toDataURL can throw on tainted/again-loading frames; skip */
      }
    }
    return out;
  }

  cleanup(): void {
    this.ctx = null;
  }
}

export default ThumbnailGenerator;
